// BILLING-PLANS (§6.1): the Plan card — the plan and its price, the live discount, the status
// with its grace date behind an (i), the renewal day and how the company pays. "Payment method"
// (owners and admins) opens Stripe's Billing Portal; there is no card form of our own.
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  InfoTip,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { CreditCardIcon } from "lucide-react";
import type { Api } from "../../../api/client";
import { formatDate, formatMoney } from "../../format";
import { bpToPercentText } from "../money";
import type { BillingOverview, SubscriptionView } from "../plan-queries";
import { SubscriptionStatusChip } from "./parts";
import { ScheduledChanges } from "./scheduled";

const APPLIES: Record<NonNullable<SubscriptionView["discount"]>["applies_to"], string> = {
  plans: "Applies to the plan, not to maintenance or credit.",
  maintenance: "Applies to maintenance, not to the plan or credit.",
  all_recurring: "Applies to the plan and maintenance, not to credit.",
};

/** "$99 / month · 5 people included · 2 extra at $15". */
/** When a discount stops: its own end, or an earlier early end (r14 #12). */
function effectiveEnd(d: NonNullable<SubscriptionView["discount"]>): string {
  const own = `${d.ends_on}T00:00:00.000Z`;
  return d.ended_at && d.ended_at < own ? d.ended_at : d.ends_on;
}

export function priceLine(sub: SubscriptionView): string {
  const p = sub.plan;
  const parts = [
    `${formatMoney(p.base_price_minor, p.currency, { whole: p.base_price_minor % 100 === 0 })} / ${p.interval}`,
    `${p.included_seats} people included`,
  ];
  if (sub.extra_seat_qty > 0)
    parts.push(
      `${sub.extra_seat_qty} extra at ${formatMoney(p.extra_seat_price_minor, p.currency, {
        whole: p.extra_seat_price_minor % 100 === 0,
      })}`,
    );
  return parts.join(" · ");
}

export function collectionText(account: BillingOverview["account"]): string {
  if (account.collection_method === "invoice") return `Invoice, Net ${account.net_terms_days}`;
  return account.card_last4 ? `Card on file ···${account.card_last4}` : "Card, none on file yet";
}

export function PlanCard({
  api,
  companyId,
  overview,
  sub,
}: {
  api: Api;
  companyId: string;
  overview: BillingOverview;
  sub: SubscriptionView;
}) {
  const portal = useMutation({
    mutationFn: () =>
      api.client.call("billing.portal", {
        params: { co: companyId },
        body: { return_url: window.location.href },
      }),
    onSuccess: (s) => window.location.assign(s.portal_url),
  });
  const d = sub.discount;
  return (
    <Card data-slot="plan-card" size="sm">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{sub.plan.name}</span>
          <SubscriptionStatusChip sub={sub} />
        </CardTitle>
        {overview.can_manage ? (
          <CardAction>
            <Button
              variant="ghost"
              size="sm"
              disabled={portal.isPending || !overview.stripe_ready}
              onClick={() => portal.mutate()}
            >
              <CreditCardIcon data-icon="inline-start" />
              Payment method
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-1.5 text-[13px]">
        <p data-field="price">{priceLine(sub)}</p>
        {d ? (
          <p data-field="discount" className="flex items-center gap-1 text-success-text">
            {d.label}: {bpToPercentText(d.percent_bp)}% off {d.live ? "until" : "from"}{" "}
            {/* ended early: in force until the earlier of that period end and its own end */}
            {formatDate(d.live ? effectiveEnd(d) : d.starts_on)}
            <InfoTip label="About the discount">{APPLIES[d.applies_to]}</InfoTip>
          </p>
        ) : null}
        <p className="text-muted-foreground">
          {sub.current_period_end && !sub.cancel_at_period_end && sub.status !== "cancelled" ? (
            <span data-field="renews">Renews on {formatDate(sub.current_period_end)} · </span>
          ) : null}
          <span data-field="collection">{collectionText(overview.account)}</span>
        </p>
        <ScheduledChanges scheduled={sub.scheduled} currency={sub.plan.currency} />
      </CardContent>
    </Card>
  );
}
